import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";

import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";

import { useEffect, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import Spinner from "presentation/components/Shared/Spinner";

import {
  useDeleteTemplateMutation,
  useFormTemplateByFilter,
} from "presentation/hooks/use-query-formtemplate";

import HeaderManageFormsGrid from "./HeaderManageFormsGrid";
import { useFilterFormTemplateStore } from "presentation/stores/use-filter-form-template-store";
import { FormsTemplateInputFilter } from "domain/models/nimberforms-input-filters";

import FormEdit from "../FormEdit";

import { Tooltip } from "primereact/tooltip";


const ManageFormsContainer = () => {
  const toast = useRef<Toast>(null);
  const deleteTemplateMutation = useDeleteTemplateMutation();
  const { filters, setFilters } = useFilterFormTemplateStore();
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [lazyParams, setLazyParams] =
    useState({
      sortBy: "createdAt",
      sortOrder: "desc",
      limit: 50,
      page: 1,
      filters: {},
    });
  const {
    data: formTemplates,
    isLoading,
    isError,
    error,
    refetch,
  } = useFormTemplateByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as FormsTemplateInputFilter);

   useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail:
        error?.message ||
        "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const handleDelete = async (id: string) => {
    try {
      if (!id)
        throw new Error("ID inválido");

      await deleteTemplateMutation.mutateAsync(id);

      refetch();

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail:
          "Formulário apagado com sucesso",
        life: 1500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail:
          error?.message ||
          "Erro ao apagar formulário",
        life: 1500,
      });
    }
  };

  const confirmAction = (
    message: string,
    acceptCallback: () => void ) => {
    confirmDialog({
      message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const showToastSuccess = (detail: string) => {
        toast.current?.show({ severity: "success", summary: "Sucesso", detail, life: 1500 });
    };

  const showActionOptions = (rowData: any) => {
    return (
      <div className="spacing-icons">

        <i
          className="pi pi-times cursor-pointer"
          data-pr-tooltip="Apagar formulário"
          onClick={() => {
            confirmAction(
              "Deseja apagar este formulário?",
              () => handleDelete(rowData.id)
            );
          }}
        />
        <i
          className="pi pi-pencil cursor-pointer"
          data-pr-tooltip="Editar formulário"
          onClick={() => {
            setSelectedTemplate(rowData);
            setShowModal(true);
          }}
        />
      </div>
    );
  };

  const tooltipStyle = {
    root: { style: { boxShadow: "none", border: "none", transition: "none", animation: "none" } },
    text: { style: { whiteSpace: "nowrap" as const } },
    arrow: { style: { display: "none" } },
  };

  return (
    <div>
      <Tooltip target="[data-pr-tooltip]" position="top" pt={tooltipStyle} />
      <Dialog
        header="Editar Formulário"
        visible={showModal}
        onHide={() =>
          setShowModal(false)
        }
        style={{
            width: "70vw",
            height: "90vh",
        }}
        contentStyle={{
            overflowY: "hidden",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
        }}
      >
        <FormEdit
          guid={selectedTemplate?.id}
          refetchGrid={() => {
            refetch();
          }}
          onCloseSuccess={() => {
            setShowModal(false);
            showToastSuccess("Formulário atualizado com sucesso");
          }}
          onClose={() => setShowModal(false)}
        />
      </Dialog>
      <DataTable
        value={formTemplates?.data ?? []}
        lazy
        paginator
        first={(lazyParams.page - 1)* lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={formTemplates?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
        setLazyParams({
        ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
        })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1 }
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1
                ? "desc"
                : e.sortOrder === 1
                ? "asc"
                : "",
          })
        }
        filters={filters}
        onFilter={(
          e: DataTableFilterEvent
        ) => {setFilters(e.filters)}
        }
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={
          <HeaderManageFormsGrid
            refetch={() => {
              refetch();
            }}
          />
        }
        rowsPerPageOptions={[
          2,
          10,
          25,
          50,
        ]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={[
          "title",
          "description",
          "createdAt",
          "modifiedAt",
        ]}
      >
        <Column
          field="title"
          header="Nome"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="description"
          header="Descrição"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="createdAt"
          header="Data de Criação"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) =>
            new Date(
              rowData.createdAt
            )
              .toISOString()
              .split("T")[0]
              .split("-")
              .reverse()
              .join("-")
          }
          filterElement={(options) => (
            <Calendar
              value={options.value ? new Date(options.value) : null}
              onChange={(e) =>
                options.filterApplyCallback(
                  e.value ? e.value.toISOString() : null
                )}
              dateFormat="dd-mm-yy"
              showIcon
              className="w-full"
              placeholder="Selecionar data"
              showButtonBar
            />)}
        />
        <Column
          field="modifiedAt"
          header="Data de Atualização"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) => {
            if (rowData.modifiedAt)
              return new Date(
                rowData.modifiedAt
              )
                .toISOString()
                .split("T")[0]
                .split("-")
                .reverse()
                .join("-");

            return "-";
          }}
          filterElement={(options) => (
            <Calendar
              value={options.value ? new Date(options.value) : null}
              onChange={(e) =>
                options.filterApplyCallback(
                  e.value ? e.value.toISOString() : null
                )}
              dateFormat="dd-mm-yy"
              showIcon
              className="w-full"
              placeholder="Selecionar data"
              showButtonBar
            />)}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast
        ref={toast}
      />
      <ConfirmDialog />
    </div>
  );
};

export default ManageFormsContainer;